import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import {
  Check as ApproveIcon,
  Send as PostIcon,
  Block as VoidIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount } from '../../../utils/formatters';
import { useAppSnackbar, useConfirmDialog } from '../../common/TableDesign';
import LedgerListPage from '../LedgerListPage';
import LedgerFormDialog from '../LedgerFormDialog';
import { CustomerPicker, LedgerPicker } from '../LedgerPickers';
import { useLedgerLookups } from '../Journals';
import { ArStatusChip, fmtDate, getErrorMessage, unwrap, unwrapList } from './ArShared';

const STATUSES = ['DRAFT', 'APPROVED', 'POSTED', 'VOID'];

const studentLabel = (w, look) => {
  if (w?.customer) return w.customer.name || `Customer ${w.customer_id}`;
  return look.customerLabel(w?.customer_id, `Customer ${w?.customer_id ?? ''}`);
};

const emptyForm = () => ({
  customer_id: '', invoice_id: '', write_off_date: new Date().toISOString().slice(0, 10),
  amount: '', reason: '',
});

// Writing off the remaining balance of a student's invoice as
// uncollectable. Same field-contract caveat as ProvisionsTab: this
// endpoint isn't exercised elsewhere in the repo, so the payload follows
// the established convention. DRAFT -> APPROVED -> POSTED lifecycle;
// approve/void run as checker, create/post as maker.
const WriteOffsTab = () => {
  const { can } = useAccessControl();
  const canCreate = can(RESOURCES.LEDGER.RECEIVABLES, ACTIONS.CREATE);
  const canApprove = can(RESOURCES.LEDGER.RECEIVABLES, ACTIONS.APPROVE);
  const canDelete = can(RESOURCES.LEDGER.RECEIVABLES, ACTIONS.DELETE);
  const look = useLedgerLookups();
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [values, setValues] = useState(emptyForm());
  const [openInvoices, setOpenInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [voidDlg, setVoidDlg] = useState({ open: false, item: null, void_reason: '' });

  const reload = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    if (!values.customer_id) {
      setOpenInvoices([]);
      return;
    }
    let alive = true;
    setLoadingInvoices(true);
    ledgerAPI.getReceivableInvoices({ customer_id: values.customer_id, limit: 100 })
      .then((res) => {
        if (!alive) return;
        setOpenInvoices(unwrapList(res).filter((inv) => Number(inv.balance_due) > 0));
      })
      .catch(() => { if (alive) setOpenInvoices([]); })
      .finally(() => { if (alive) setLoadingInvoices(false); });
    return () => { alive = false; };
  }, [values.customer_id]);

  const invoiceOptions = openInvoices.map((inv) => ({
    value: inv.invoice_id,
    label: `${inv.invoice_number || `INV-${inv.invoice_id}`} - Balance ${formatAccountingAmount(inv.balance_due)}`,
  }));

  const selectInvoice = (invoiceId) => {
    const inv = openInvoices.find((i) => String(i.invoice_id) === String(invoiceId));
    setValues((p) => ({ ...p, invoice_id: invoiceId, amount: inv ? String(inv.balance_due) : p.amount }));
  };

  const submitCreate = async () => {
    if (!values.customer_id) {
      showSnackbar('Student is required', 'error');
      return;
    }
    if (!values.invoice_id) {
      showSnackbar('Select the invoice to write off', 'error');
      return;
    }
    if (!(Number(values.amount) > 0)) {
      showSnackbar('Amount must be greater than zero', 'error');
      return;
    }
    const payload = {
      customer_id: Number(values.customer_id),
      invoice_id: Number(values.invoice_id),
      write_off_date: values.write_off_date,
      amount: Number(values.amount),
    };
    if (values.reason?.trim()) payload.reason = values.reason.trim();
    try {
      setSaving(true);
      await ledgerAPI.createArWriteOff(payload);
      showSnackbar('Write-off created as DRAFT', 'success');
      setShowCreate(false);
      setValues(emptyForm());
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const runWorkflow = (label, pastTense, fn) => {
    confirm({
      title: label,
      message: `Are you sure you want to ${label.toLowerCase()} this write-off?`,
      onConfirm: async () => {
        try {
          await fn();
          showSnackbar(`Write-off ${pastTense} successfully`, 'success');
          reload();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  const openDetail = async (row) => {
    try {
      const res = await ledgerAPI.getArWriteOff(row.write_off_id);
      setDetail(unwrap(res));
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch write-off'), 'error');
    }
  };

  const submitVoid = async () => {
    if ((voidDlg.void_reason?.trim() ?? '').length < 5) {
      showSnackbar('Void reason (min 5 chars) is required', 'error');
      return;
    }
    try {
      await ledgerAPI.voidArWriteOff(voidDlg.item.write_off_id, { void_reason: voidDlg.void_reason.trim() });
      showSnackbar('Write-off voided successfully', 'success');
      setVoidDlg({ open: false, item: null, void_reason: '' });
      setDetail(null);
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  return (
    <>
      <LedgerListPage
        resource={RESOURCES.LEDGER.RECEIVABLES}
        title="Write-offs"
        subtitle="Uncollectable student invoice balances written off"
        addLabel="New Write-off"
        idKey="write_off_id"
        searchPlaceholder="Search write-off #, student..."
        emptyMessage="No write-offs found."
        refreshKey={refreshKey}
        fetchItems={(params) => ledgerAPI.getArWriteOffs(params)}
        onAdd={canCreate ? () => { setValues(emptyForm()); setShowCreate(true); } : null}
        filters={[
          { key: 'status', label: 'Status', options: STATUSES.map((s) => ({ label: s, value: s })) },
          { type: 'picker', key: 'customer_id', label: 'Student', picker: CustomerPicker },
          { type: 'daterange', keyFrom: 'start_date', keyTo: 'end_date', label: 'Write-off date' },
          { type: 'numberrange', keyFrom: 'min_amount', keyTo: 'max_amount', label: 'amount' },
        ]}
        columns={[
          { label: 'Write-off #', render: (w) => <strong>{w.write_off_number || `WO-${w.write_off_id}`}</strong> },
          { label: 'Student', render: (w) => studentLabel(w, look) },
          { label: 'Invoice', render: (w) => w.invoice?.invoice_number || `INV-${w.invoice_id}`, hideOnMobile: true },
          { label: 'Date', render: (w) => fmtDate(w.write_off_date) },
          { label: 'Amount', render: (w) => formatAccountingAmount(w.amount), align: 'right' },
          { label: 'Status', render: (w) => <ArStatusChip status={w.status} /> },
        ]}
        rowActions={(w) => {
          const actions = [{ icon: ViewIcon, title: 'View detail', color: 'default', onClick: () => openDetail(w) }];
          if (w.status === 'DRAFT' && canApprove) {
            actions.push({ icon: ApproveIcon, title: 'Approve', color: 'primary', onClick: () => runWorkflow('Approve', 'approved', () => ledgerAPI.approveArWriteOff(w.write_off_id)) });
          }
          if (w.status === 'APPROVED' && canApprove) {
            actions.push({ icon: PostIcon, title: 'Post', color: 'primary', onClick: () => runWorkflow('Post', 'posted', () => ledgerAPI.postArWriteOff(w.write_off_id)) });
          }
          if (['DRAFT', 'APPROVED', 'POSTED'].includes(w.status) && canApprove) {
            actions.push({ icon: VoidIcon, title: 'Void', color: 'error', onClick: () => setVoidDlg({ open: true, item: w, void_reason: '' }) });
          }
          if (w.status === 'DRAFT' && canDelete) {
            actions.push({
              icon: DeleteIcon, title: 'Delete draft', color: 'error',
              onClick: () => confirm({
                title: 'Delete write-off',
                message: `Delete DRAFT write-off ${w.write_off_number || `WO-${w.write_off_id}`}? This cannot be undone.`,
                onConfirm: async () => {
                  try {
                    await ledgerAPI.deleteArWriteOff(w.write_off_id);
                    showSnackbar('Write-off deleted', 'success');
                    reload();
                  } catch (error) {
                    showSnackbar(getErrorMessage(error), 'error');
                  }
                },
              }),
            });
          }
          return actions;
        }}
      />

      <Dialog open={showCreate} onClose={() => setShowCreate(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Write-off</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid size={12}>
              <CustomerPicker
                label="Student" value={values.customer_id}
                onChange={(id) => setValues({ ...emptyForm(), customer_id: id })}
                required
              />
            </Grid>
            <Grid size={12}>
              {!values.customer_id ? (
                <Typography variant="body2" color="text.secondary">Select a student to load their open invoices.</Typography>
              ) : loadingInvoices ? (
                <Typography variant="body2" color="text.secondary">Loading open invoices...</Typography>
              ) : openInvoices.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No open invoices for this student.</Typography>
              ) : (
                <LedgerPicker label="Invoice to write off" value={values.invoice_id} onChange={selectInvoice} options={invoiceOptions} required />
              )}
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth size="small" type="date" label="Write-off Date" value={values.write_off_date}
                onChange={(e) => setValues((p) => ({ ...p, write_off_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth size="small" type="number" label="Amount" value={values.amount}
                onChange={(e) => setValues((p) => ({ ...p, amount: e.target.value }))}
                inputProps={{ min: 0.01, step: 'any' }} required
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth size="small" multiline rows={2} label="Reason" value={values.reason}
                onChange={(e) => setValues((p) => ({ ...p, reason: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitCreate} disabled={saving}>
            {saving ? 'Saving...' : 'Create Draft'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Write-off {detail?.write_off_number || (detail?.write_off_id ? `WO-${detail.write_off_id}` : '')}</DialogTitle>
        <DialogContent>
          {detail && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>Student: <strong>{studentLabel(detail, look)}</strong></Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>Invoice: {detail.invoice?.invoice_number || `INV-${detail.invoice_id}`}</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>Date: {fmtDate(detail.write_off_date)}</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>Amount: <strong>{formatAccountingAmount(detail.amount)}</strong></Typography>
              {detail.reason && <Typography variant="body2" sx={{ mb: 1 }}>Reason: {detail.reason}</Typography>}
              <Box sx={{ mt: 1 }}><ArStatusChip status={detail.status} /></Box>
              {detail.status === 'VOID' && detail.void_reason && (
                <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>Voided: {detail.void_reason}</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <LedgerFormDialog
        open={voidDlg.open}
        onClose={() => setVoidDlg({ open: false, item: null, void_reason: '' })}
        title={`Void write-off ${voidDlg.item?.write_off_number || (voidDlg.item ? `WO-${voidDlg.item.write_off_id}` : '')}`}
        onSubmit={submitVoid}
        values={voidDlg}
        onChange={(key, value) => setVoidDlg((p) => ({ ...p, [key]: value }))}
        maxWidth="sm"
        fields={[{ key: 'void_reason', label: 'Void Reason (min 5 chars)', type: 'textarea', required: true, md: 12 }]}
      />

      {ConfirmDialogComponent}
      {SnackbarComponent}
    </>
  );
};

export default WriteOffsTab;
